import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import {
  selectLiterature,
  generateQuestionsStart,
  generateQuestionsSuccess,
  generateQuestionsFailure,
  resetLiteratures,
  searchLiteratureStart,
  searchLiteratureSuccess,
  searchLiteratureFailure
} from '../store/slices/appSlice'
import { generateQuestions, searchLiterature } from '../services/api'
import type { Literature } from '@shared/types'

export default function LiteraturePage() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const {
    literatures,
    selectedLiterature,
    selectedCategory,
    keyword,
    apiKey,
    selectedModel,
    isGeneratingQuestions
  } = useAppSelector((state) => state.app)

  const handleSelectLiterature = async (literature: Literature) => {
    dispatch(selectLiterature(literature))
    dispatch(generateQuestionsStart())

    try {
      const questions = await generateQuestions(
        literature,
        apiKey,
        selectedModel
      )
      dispatch(generateQuestionsSuccess(questions))
      navigate('/questions')
    } catch (error) {
      dispatch(generateQuestionsFailure(
        error instanceof Error ? error.message : '题目生成失败'
      ))
    }
  }

  const handleBackToHome = () => {
    dispatch(resetLiteratures())
    navigate('/')
  }

  const handleRefreshLiteratures = async () => {
    if (!selectedCategory || !keyword || !apiKey) {
      return
    }

    setIsRefreshing(true)
    dispatch(searchLiteratureStart())

    try {
      // 传递当前文献列表，让AI排除这些已经展示过的文献
      const excludeLiteratures = literatures.map(lit => ({
        title: lit.title,
        authors: lit.authors,
        year: lit.year
      }))

      const newLiteratures = await searchLiterature(
        selectedCategory,
        keyword,
        apiKey,
        selectedModel,
        excludeLiteratures  // 传递排除列表
      )
      dispatch(searchLiteratureSuccess(newLiteratures))
    } catch (error) {
      dispatch(searchLiteratureFailure(
        error instanceof Error ? error.message : '文献检索失败'
      ))
    } finally {
      setIsRefreshing(false)
    }
  }

  if (literatures.length === 0) {
    return (
      <div className="max-w-4xl mx-auto text-center space-y-6">
        <div className="card">
          <div className="py-12">
            <p className="text-gray-600 mb-4">暂无文献数据</p>
            <button
              onClick={handleBackToHome}
              className="btn btn-primary"
            >
              返回首页
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* 标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">文献列表</h1>
          <p className="text-gray-600 mt-2">
            {selectedCategory} - {keyword}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefreshLiteratures}
            disabled={isRefreshing}
            className="btn btn-primary"
          >
            {isRefreshing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                刷新中...
              </>
            ) : (
              '🔄 换一组文献'
            )}
          </button>
          <button
            onClick={handleBackToHome}
            className="btn btn-secondary"
          >
            返回首页
          </button>
        </div>
      </div>

      {/* 文献列表 */}
      <div className="space-y-4">
        {literatures.map((literature, index) => (
          <div
            key={index}
            className={`card cursor-pointer transition-all ${
              selectedLiterature?.title === literature.title
                ? 'ring-2 ring-primary-600 bg-primary-50'
                : 'hover:shadow-lg'
            }`}
            onClick={() => !isGeneratingQuestions && handleSelectLiterature(literature)}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {index + 1}. {literature.title}
                </h3>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                  <span>作者: {literature.authors}</span>
                  <span>年份: {literature.year}</span>
                </div>
                {literature.abstract && (
                  <p className="text-sm text-gray-600 mt-3 line-clamp-2">
                    {literature.abstract}
                  </p>
                )}
                <a
                  href={literature.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-sm text-primary-600 hover:text-primary-700 mt-2 inline-block"
                >
                  查看原文 →
                </a>
              </div>

              {selectedLiterature?.title === literature.title && isGeneratingQuestions && (
                <div className="flex items-center gap-2 text-sm text-primary-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent"></div>
                  <span>生成中...</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 提示 */}
      <div className="card bg-blue-50 border-2 border-blue-200">
        <p className="text-sm text-gray-700">
          点击任意文献卡片,系统将自动基于该文献生成 3 道选择题及解析
        </p>
      </div>
    </div>
  )
}
